/**
 * Focus Control Hook
 * 
 * Custom React hook for managing Focus Mode state with three modes:
 * 1. Timer Mode - Short focus sessions (1-999 minutes)
 * 2. Block Mode - Longer feed blocks (1-24 hours)
 * 3. Time Window - Recurring daily time windows
 * 
 * State is persisted to localStorage for cross-session persistence.
 */

import { useState, useEffect, useCallback } from 'react';
import { 
  FocusControlState, 
  FocusMode,
  FocusTimerSettings,
  FeedBlockSettings,
  TimeWindowSettings
} from '../types/focusControl.types';
import { freezeScroll, unfreezeScroll } from '../utils/scrollFreeze';

const STORAGE_KEY = 'superkonnected_focus_control';

// Default state
const DEFAULT_STATE: FocusControlState = {
  activeMode: 'none',
  isBlocked: false,
  timer: { durationMinutes: 15, startedAt: null, endsAt: null },
  block: { durationHours: 4, startedAt: null, endsAt: null },
  window: { enabled: false, startTime: '12:00', endTime: '15:00' },
  remainingSeconds: 0
};

export function useFocusControl() {
  const [state, setState] = useState<FocusControlState>(DEFAULT_STATE);

  // Load settings from localStorage on mount
  useEffect(() => {
    try {
      const saved = localStorage.getItem(STORAGE_KEY);
      if (saved) {
        const parsed = JSON.parse(saved);
        
        // Convert date strings back to Date objects
        if (parsed.timer.startedAt) parsed.timer.startedAt = new Date(parsed.timer.startedAt);
        if (parsed.timer.endsAt) parsed.timer.endsAt = new Date(parsed.timer.endsAt);
        if (parsed.block.startedAt) parsed.block.startedAt = new Date(parsed.block.startedAt);
        if (parsed.block.endsAt) parsed.block.endsAt = new Date(parsed.block.endsAt);
        
        setState(prev => ({ ...prev, ...parsed }));
      }
    } catch (error) {
      console.error('❌ Error loading focus control settings:', error);
    }
  }, []);

  // Save settings to localStorage whenever state changes
  const saveSettings = useCallback((newState: Partial<FocusControlState>) => {
    setState(prev => {
      const updated = { ...prev, ...newState };
      
      // Save to localStorage (async, doesn't block)
      try {
        localStorage.setItem(STORAGE_KEY, JSON.stringify(updated));
      } catch (error) {
        console.error('❌ Error saving focus control settings:', error);
      }
      
      return updated;
    });
  }, []);

  // Check if currently within time window
  const checkTimeWindow = useCallback(() => {
    if (!state.window.enabled) return false;
    
    const now = new Date();
    const currentTime = `${now.getHours().toString().padStart(2, '0')}:${now.getMinutes().toString().padStart(2, '0')}`;
    
    // Handle time windows that cross midnight
    const { startTime, endTime } = state.window;
    if (startTime <= endTime) {
      // Normal window (e.g., 12:00 - 15:00)
      return currentTime >= startTime && currentTime <= endTime;
    } else {
      // Crosses midnight (e.g., 22:00 - 02:00)
      return currentTime >= startTime || currentTime <= endTime;
    }
  }, [state.window]);

  // Start focus timer
  const startTimer = useCallback((minutes: number) => {
    const now = new Date();
    const endsAt = new Date(now.getTime() + minutes * 60 * 1000);
    
    saveSettings({
      activeMode: 'timer',
      isBlocked: true,
      timer: { 
        durationMinutes: minutes, 
        startedAt: now, 
        endsAt 
      }
    });
    
    freezeScroll();
    console.log(`⏱️ Focus Timer started: ${minutes} minutes`);
  }, [saveSettings]);

  // Start feed block
  const startBlock = useCallback((hours: number) => {
    const now = new Date();
    const endsAt = new Date(now.getTime() + hours * 60 * 60 * 1000);
    
    saveSettings({
      activeMode: 'block',
      isBlocked: true,
      block: { 
        durationHours: hours, 
        startedAt: now, 
        endsAt 
      }
    });
    
    freezeScroll();
    console.log(`🚫 Feed Block started: ${hours} hours`);
  }, [saveSettings]);

  // Toggle time window
  const toggleTimeWindow = useCallback((enabled: boolean, startTime?: string, endTime?: string) => {
    const newWindow: TimeWindowSettings = {
      enabled,
      startTime: startTime || state.window.startTime,
      endTime: endTime || state.window.endTime
    };
    
    saveSettings({
      window: newWindow
    });
    
    console.log(`🕐 Time Window ${enabled ? 'enabled' : 'disabled'}: ${newWindow.startTime} - ${newWindow.endTime}`);
  }, [state.window, saveSettings]);

  // Update time window settings (without toggling enabled state)
  const updateTimeWindow = useCallback((startTime: string, endTime: string) => {
    saveSettings({
      window: {
        ...state.window,
        startTime,
        endTime
      }
    });
  }, [state.window, saveSettings]);

  // Stop active focus mode
  const stopFocus = useCallback(() => {
    saveSettings({
      activeMode: 'none',
      isBlocked: false,
      timer: { ...state.timer, startedAt: null, endsAt: null },
      block: { ...state.block, startedAt: null, endsAt: null }
    });
    
    unfreezeScroll();
    console.log('✅ Focus Mode stopped');
  }, [state, saveSettings]);

  // Countdown ticker and time window checker
  useEffect(() => {
    const interval = setInterval(() => {
      // Check time window first (highest priority)
      const inWindow = checkTimeWindow();
      if (inWindow && !state.isBlocked) {
        // Enter time window block
        setState(prev => ({ ...prev, isBlocked: true }));
        freezeScroll();
        console.log('🕐 Entered time window block');
        return;
      } else if (!inWindow && state.isBlocked && state.activeMode === 'none') {
        // Exit time window block
        setState(prev => ({ ...prev, isBlocked: false }));
        unfreezeScroll();
        console.log('🕐 Exited time window block');
        return;
      }

      // Check timer/block expiration
      if (state.activeMode === 'timer' && state.timer.endsAt) {
        const now = Date.now();
        const endsAt = new Date(state.timer.endsAt).getTime();
        const remaining = Math.max(0, Math.floor((endsAt - now) / 1000));
        
        setState(prev => ({ ...prev, remainingSeconds: remaining }));
        
        if (remaining === 0) {
          console.log('⏱️ Focus Timer completed');
          stopFocus();
        }
      } else if (state.activeMode === 'block' && state.block.endsAt) {
        const now = Date.now();
        const endsAt = new Date(state.block.endsAt).getTime();
        const remaining = Math.max(0, Math.floor((endsAt - now) / 1000));
        
        setState(prev => ({ ...prev, remainingSeconds: remaining }));
        
        if (remaining === 0) {
          console.log('🚫 Feed Block completed');
          stopFocus();
        }
      }
    }, 1000); // Check every second

    return () => clearInterval(interval);
  }, [state, checkTimeWindow, stopFocus]);

  // Apply/remove scroll freeze when isBlocked changes
  useEffect(() => {
    if (state.isBlocked) {
      freezeScroll();
    } else {
      unfreezeScroll();
    }
    
    // Cleanup on unmount
    return () => {
      unfreezeScroll();
    };
  }, [state.isBlocked]);

  return {
    state,
    startTimer,
    startBlock,
    toggleTimeWindow,
    updateTimeWindow,
    stopFocus,
    isBlocked: state.isBlocked || checkTimeWindow()
  };
}
